'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { CaseStatus } from './StatusBadge';
import { AppType } from './AppTypeBadge';

interface FilterState {
  search: string;
  status: CaseStatus | '';
  appType: AppType | '';
  dateFrom: string;
  dateTo: string;
  sortField: string;
  sortDir: 'asc' | 'desc';
}

interface CaseTableFiltersProps {
  filters: FilterState;
  totalCount: number;
  filteredCount: number;
  onFilterChange: (filters: FilterState) => void;
  onCreateNew: () => void;
}

const STATUS_OPTIONS: { value: CaseStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'APPLICATION_CREATED', label: 'Created' },
  { value: 'APPLICATION_SUBMITTED', label: 'Submitted' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'MORE_INFO_REQUIRED', label: 'More Info Required' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const TYPE_OPTIONS: { value: AppType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'ARF', label: 'ARF' },
  { value: 'PRB', label: 'PRB' },
  { value: 'PRSA', label: 'PRSA' },
];

const CaseTableFilters = ({ filters, totalCount, filteredCount, onFilterChange, onCreateNew }: CaseTableFiltersProps) => {
  const update = (partial: Partial<FilterState>) => onFilterChange({ ...filters, ...partial });

  const clearFilters = () => {
    onFilterChange({ search: '', status: '', appType: '', dateFrom: '', dateTo: '', sortField: 'submissionDate', sortDir: 'desc' });
  };

  const hasActiveFilters = filters.search || filters.status || filters.appType || filters.dateFrom || filters.dateTo;

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <Icon name="MagnifyingGlassIcon" size={16} variant="outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
          <input
            type="text"
            placeholder="Search by client name, case ID..."
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-md bg-input text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-standard"
          />
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            value={filters.status}
            onChange={(e) => update({ status: e.target.value as CaseStatus | '' })}
            className="appearance-none pl-3 pr-8 py-2 text-sm border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-standard cursor-pointer min-w-[160px]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        </div>

        {/* App type filter */}
        <div className="relative">
          <select
            value={filters.appType}
            onChange={(e) => update({ appType: e.target.value as AppType | '' })}
            className="appearance-none pl-3 pr-8 py-2 text-sm border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-standard cursor-pointer min-w-[130px]"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => update({ dateFrom: e.target.value })}
            className="px-3 py-2 text-sm border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-standard"
            title="From date"
          />
          <span className="text-text-secondary text-xs">to</span>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => update({ dateTo: e.target.value })}
            className="px-3 py-2 text-sm border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-standard"
            title="To date"
          />
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-text-secondary hover:text-error hover:bg-error/5 rounded-md transition-standard"
          >
            <Icon name="XMarkIcon" size={14} variant="outline" />
            <span>Clear</span>
          </button>
        )}

        {/* Create new */}
        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 transition-standard shadow-elevation-1 whitespace-nowrap ml-auto lg:ml-0"
        >
          <Icon name="PlusIcon" size={16} variant="outline" />
          <span>New Application</span>
        </button>
      </div>

      {/* Result count */}
      <div className="mt-3 flex items-center gap-2">
        <p className="text-xs font-caption text-text-secondary">
          Showing <span className="font-medium text-text-primary">{filteredCount}</span> of <span className="font-medium text-text-primary">{totalCount}</span> cases
        </p>
        {hasActiveFilters && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-caption bg-primary/10 text-primary">
            Filtered
          </span>
        )}
      </div>
    </div>
  );
};

export default CaseTableFilters;
export type { FilterState };